import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Palette, Users, BadgeEuro } from "lucide-react";

const cardVariants = {
	hidden: { opacity: 0, y: 20 },
	visible: (i) => ({
		opacity: 1,
		y: 0,
		transition: { delay: i * 0.2, duration: 0.6, ease: "easeOut" },
	}),
};

export default function VisionBlock() {
	const { t } = useTranslation();

	const items = [
		{
			icon: <Palette className="w-6 h-6 text-primary" />,
			title: t("vision.items.easy.title"),
			text: t("vision.items.easy.text"),
		},
		{
			icon: <Users className="w-6 h-6 text-primary" />,
			title: t("vision.items.customizable.title"),
			text: t("vision.items.customizable.text"),
		},
		{
			icon: <BadgeEuro className="w-6 h-6 text-primary" />,
			title: t("vision.items.scalable.title"),
			text: t("vision.items.scalable.text"),
		},
	];

	return (
		<section className="py-24 bg-white">
			<div className="max-w-4xl mx-auto px-6 text-center">
				<motion.h2
					className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6"
					initial={{ opacity: 0, y: 40 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, ease: "easeOut" }}
					viewport={{ once: true }}
				>
					{t("vision.title")}{" "}
					<span className="text-primary">{t("vision.highlight")}</span>
				</motion.h2>

				<motion.p
					className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto"
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, delay: 0.2 }}
					viewport={{ once: true }}
				>
					{t("vision.text1")}{" "}
					<span className="font-semibold text-gray-900">
						{t("vision.text2")}
					</span>{" "}
					{t("vision.text3")}
				</motion.p>

				{/* Valores */}
				<div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-8">
					{items.map((item, i) => (
						<motion.div
							key={item.title}
							className="bg-white rounded-xl p-6 border border-gray-200 flex flex-col items-center text-center cursor-pointer transition-colors"
							variants={cardVariants}
							initial="hidden"
							whileInView="visible"
							custom={i}
							viewport={{ once: true }}
							whileHover={{
								scale: 1.05,
								y: -6,
								boxShadow: "0 12px 24px rgba(0,0,0,0.04)",
								borderColor: "rgb(67, 189, 152)", // primary
								transition: { duration: 0.2 },
							}}
							whileTap={{ scale: 0.97 }}
						>
							<div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary/10 mb-4">
								{item.icon}
							</div>
							<h3 className="mt-2 font-semibold text-gray-900">{item.title}</h3>
							<p className="text-sm text-gray-600 mt-2">{item.text}</p>
						</motion.div>
					))}
				</div>
			</div>
		</section>
	);
}
